/**
 * @file MenuTargetField.tsx
 * @description «Куда ведёт» пункт меню — поле по виду пункта (phase19.16 Р1;
 * uiux-design-r1 §3 «Окно пункта меню»): выбор из списка (страница,
 * категория, рубрика, тег, документ) с состоянием цели, раздел платформы,
 * адрес с «В новой вкладке», число категорий автосписка.
 *
 * Партия «Юридические документы» (uiux-legal-steps-b-c §8.1): цель
 * «Документ или группа документов» — общее поле `LegalTargetField` (группы и
 * документы с состоянием, «+ Создать» автоматической группы сертификатов).
 */

import { SITE_LIMITS, SITE_SYSTEM_ROUTES, type SiteSystemRoute } from "@chaiberi/shared";
import { useEffect } from "react";

import { Button } from "../../ui/button";
import { Combobox } from "../../ui/combobox";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { CheckRow } from "../CheckRow";
import { LegalTargetField } from "../legal/LegalTargetField";
import { Segmented } from "../Segmented";
import type { MenuTargetsApi } from "../../../hooks/site-view/useMenuTargets";
import type { MenuFormErrors, MenuItemForm } from "../../../lib/site-view/menu-form";
import { MENU_LINK_TYPE_META, SYSTEM_ROUTE_LABELS } from "../../../lib/site-view/menu-link-types";

interface Props {
  form: MenuItemForm;
  errors: MenuFormErrors;
  targets: MenuTargetsApi;
  onChange: (patch: Partial<MenuItemForm>) => void;
}

export function MenuTargetField({ form, errors, targets, onChange }: Props) {
  const list = MENU_LINK_TYPE_META[form.linkType].list;
  const { ensure } = targets;
  // Документы грузит своё поле (`useLegalTargets`) — общий список не нужен.
  useEffect(() => {
    if (list && list !== "legalDocuments") ensure(list);
  }, [list, ensure]);

  if (list === "legalDocuments") {
    return (
      <div className="flex flex-col gap-1">
        <Label htmlFor="menu-target">{MENU_LINK_TYPE_META[form.linkType].label}</Label>
        <LegalTargetField
          id="menu-target"
          value={form.targetId}
          invalid={!!errors.target}
          menuHint
          // Подпись подставляется из названия цели, если её ещё не писали.
          onChange={(targetId, title) => onChange({ targetId, label: form.label || title })}
        />
        {errors.target ? <p className="type-caption text-destructive">{errors.target}</p> : null}
      </div>
    );
  }

  if (list) {
    const options = (targets.lists[list] ?? []).map((t) => ({ value: t.id, label: t.label, description: t.description }));
    return (
      <div className="flex flex-col gap-1">
        <Label htmlFor="menu-target">{MENU_LINK_TYPE_META[form.linkType].label}</Label>
        {targets.failed[list] ? (
          <div className="flex flex-col items-start gap-2">
            <p className="type-caption text-on-surface-variant">
              Не удалось загрузить список. Можно выбрать вид «Другой сайт или адрес» и указать адрес на этом сайте, например /about.
            </p>
            <Button variant="outline" onClick={() => ensure(list, true)}>
              Повторить
            </Button>
          </div>
        ) : (
          <Combobox
            id="menu-target"
            options={options}
            value={form.targetId}
            invalid={!!errors.target}
            placeholder={targets.lists[list] ? "Выберите…" : "Загружаем…"}
            onChange={(targetId) => {
              const picked = options.find((o) => o.value === targetId);
              // Подпись подставляется из названия цели, если её ещё не писали.
              onChange({ targetId, label: form.label || picked?.label || "" });
            }}
          />
        )}
        {errors.target ? <p className="type-caption text-destructive">{errors.target}</p> : null}
      </div>
    );
  }

  if (form.linkType === "SYSTEM") {
    return (
      <div className="flex flex-col gap-1">
        <Label>Раздел</Label>
        <Segmented
          aria-label="Раздел сайта"
          options={SITE_SYSTEM_ROUTES.map((r) => ({ value: r, label: SYSTEM_ROUTE_LABELS[r] }))}
          value={form.systemRoute ?? ("" as SiteSystemRoute)}
          onChange={(systemRoute) => onChange({ systemRoute, label: form.label || SYSTEM_ROUTE_LABELS[systemRoute] })}
        />
        {errors.target ? <p className="type-caption text-destructive">{errors.target}</p> : null}
      </div>
    );
  }

  if (form.linkType === "URL") {
    return (
      <div className="flex flex-col gap-1">
        <Label htmlFor="menu-url">Адрес</Label>
        <Input
          id="menu-url"
          inputMode="url"
          placeholder="https://… или /about"
          value={form.url}
          aria-invalid={!!errors.url}
          onChange={(e) => onChange({ url: e.target.value })}
        />
        {errors.url ? <p className="type-caption text-destructive">{errors.url}</p> : null}
        <CheckRow checked={form.openInNewTab} onChange={(openInNewTab) => onChange({ openInNewTab })}>
          В новой вкладке
        </CheckRow>
      </div>
    );
  }

  if (form.linkType === "CATALOG_CATEGORIES") {
    return (
      <div className="flex flex-col gap-1">
        <Label htmlFor="menu-limit">Сколько категорий показать (пусто — все, до {SITE_LIMITS.catalogCategoriesAutoLimit})</Label>
        <Input
          id="menu-limit"
          type="number"
          inputMode="numeric"
          min={1}
          max={SITE_LIMITS.catalogCategoriesAutoLimit}
          className="w-32"
          value={form.autoLimit ?? ""}
          aria-invalid={!!errors.autoLimit}
          onChange={(e) => onChange({ autoLimit: e.target.value === "" ? null : Number(e.target.value) })}
        />
        {errors.autoLimit ? <p className="type-caption text-destructive">{errors.autoLimit}</p> : null}
        <p className="type-caption text-on-surface-variant">Категории собираются сами из опубликованных в каталоге.</p>
      </div>
    );
  }

  return <p className="type-caption text-on-surface-variant">Группа без ссылки — заголовок колонки для вложенных пунктов.</p>;
}
